import React, { memo, useCallback, useMemo, useRef } from 'react';

import {
  CellPosition,
  CellValueChangedEvent,
  DetailGridInfo,
  GridReadyEvent,
  IRowNode,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { WithLocalStorage } from '@/components/hoc';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useContextMenu } from '@/utils/context-menu';

import { useGridSelectionRestore } from '../hooks/useGridSelectionRestore';
import { getFreezeIntEmbryoColDefs } from '../utils/columnDefs';
import { SelectionService } from '../utils/selectionService';
import { TempEmbryoGridProps } from '../utils/types';
import { AddFreezeEmbryoControls } from './AddFreezeEmbryoControls';

export const TempEmbryoGrid: React.FC<TempEmbryoGridProps> = memo(
  ({
    tempEmbryos,
    gridRef,
    rowData,
    onEdit,
    handleUpdateTempEmbryo,
    handleTempEmbryoSelectionChanged,
    handleAddTempEmbryos,
    handleDeactivateTempEmbryo,
    refetchEmbryosFreezeData,
    isExport,
    mode,
    isUpdateRunning,
    node,
    api,
    isExpanded,
  }) => {
    const isCaningColorCodeEnabled = useFeatureFlag(
      'Freeze_TemplateColor_Coded',
    );
    const defaultGridOptions = useDefaultGridOptions();
    // Audit lookup uses the embryo entity GUID (embryoId), not the
    // freeze-entry tracking id used for ag-grid row identity.
    const { getContextMenuItems } = useContextMenu({ idKey: 'embryoId' });
    const rowId = node.id!;

    const { wrappedSelectionChanged, onModelUpdated } = useGridSelectionRestore(
      {
        embryos: tempEmbryos,
        getSelectedIds: (selection) => selection.tempEmbryoIds,
        getNodeId: (node) => node.data.embryoFreezeEntryId,
        onSelectionChanged: handleTempEmbryoSelectionChanged,
      },
    );

    const handleCellValueChanged = useCallback(
      (params: CellValueChangedEvent) => {
        handleUpdateTempEmbryo(params);
        onEdit(params.data.fertilizationId);
      },
      [handleUpdateTempEmbryo, onEdit],
    );

    const handleGridReady = useCallback(
      (params: GridReadyEvent) => {
        const gridInfo: DetailGridInfo = {
          id: rowId,
          api: params.api,
        };

        api.addDetailGridInfo(rowId, gridInfo);

        const selection = SelectionService.getSelection();
        params.api.forEachNode((node: IRowNode) => {
          const isSelected = selection.tempEmbryoIds.includes(
            node.data.embryoFreezeEntryId,
          );
          node.setSelected(isSelected);
        });
      },
      [api, rowId],
    );

    const handleCellKeyDown = (event: any) => {
      if (event.event.key === 'Enter') {
        const currentCell: CellPosition = event.api.getFocusedCell();
        if (currentCell) {
          const nextRowIndex = currentCell.rowIndex + 1;
          event.api.setFocusedCell(nextRowIndex, currentCell.column);
          event.api.clearRangeSelection();
        }
      }
    };

    // Stable column-def reference so re-renders during a cell edit (e.g.
    // isUpdateRunning toggling in onCellValueChanged) don't hand ag-grid a new
    // columnDefs array, which rebuilds the columns and steals focus from the
    // cell the user just tabbed into.
    const stableIntEmbryoColDefs = useMemo(
      () => getFreezeIntEmbryoColDefs(),
      [],
    );

    // Stable context & gridOptions refs so ag-grid doesn't reset focus on
    // re-renders caused by isUpdateRunning toggling during cell edits.
    // We mutate the same object in-place so ag-grid sees a stable reference
    // but cell renderers always read the latest values via params.context.
    const stableContextRef = useRef<Record<string, unknown> | null>(null);
    if (!stableContextRef.current) {
      stableContextRef.current = {};
    }
    Object.assign(stableContextRef.current, {
      isExport,
      isUpdateRunning,
      partnershipId: rowData?.partnershipId,
      billAccountCaningOption: rowData?.billAccountCaningOption,
      isCaningColorCodeEnabled,
    });

    const stableGridOptions = useMemo(
      () => ({
        ...defaultGridOptions,
        domLayout: isExpanded ? ('autoHeight' as const) : ('normal' as const),
        suppressColumnVirtualisation: false,
        suppressRowVirtualisation: false,
        enableCellTextSelection: false,
        suppressDragLeaveHidesColumns: true,
        suppressBrowserResizeObserver: true,
        suppressAnimationFrame: true,
        suppressScrollOnNewData: true,
        suppressModelUpdateAfterUpdateTransaction: true,
        suppressAggFuncInHeader: true,
        suppressAggAtRootLevel: true,
        suppressRowTransform: true,
        suppressColumnMoveAnimation: true,
        suppressRowHoverHighlight: true,
        suppressRowDeselection: true,
        suppressRowDrag: true,
        rowBuffer: 20,
        cacheBlockSize: 50,
        maxBlocksInCache: 10,
        animateRows: false,
      }),
      [defaultGridOptions, isExpanded],
    );

    return (
      <div className="flex flex-row gap-4">
        <AddFreezeEmbryoControls
          onAdd={handleAddTempEmbryos}
          onDeactivate={handleDeactivateTempEmbryo}
          refetch={refetchEmbryosFreezeData}
          rowData={rowData}
        />
        <div className="w-full">
          <div className="mb-2">
            <h3>Temporary Embryos</h3>
          </div>
          <div
            className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} w-full ${isExpanded ? '' : 'h-[14rem] overflow-y-auto'}`}
          >
            <WithLocalStorage storageKey="freeze-temp-embryo-grid">
              <AgGridReact
                ref={gridRef}
                className="h-full w-full"
                rowData={tempEmbryos}
                defaultColDef={defaultColDef}
                columnDefs={stableIntEmbryoColDefs}
                masterDetail={true}
                stopEditingWhenCellsLoseFocus={true}
                onCellValueChanged={handleCellValueChanged}
                onCellKeyDown={handleCellKeyDown}
                suppressRowClickSelection={true}
                onSelectionChanged={wrappedSelectionChanged}
                onModelUpdated={onModelUpdated}
                rowSelection="multiple"
                context={stableContextRef.current}
                gridOptions={stableGridOptions}
                getRowId={(params) => params.data.embryoFreezeEntryId}
                getContextMenuItems={getContextMenuItems}
                onGridReady={handleGridReady}
              />
            </WithLocalStorage>
          </div>
        </div>
      </div>
    );
  },
);

TempEmbryoGrid.displayName = 'TempEmbryoGrid';
